import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useHistory } from "react-router-dom";
import { shiftPalette, shiftCardSx, SHIFT_CATEGORIES } from "./shiftHelpers";
import { designWeights } from "services/constants/designTokens";

// Right column: Shift Summary counts (for the loaded period) and Quick Links.
// Counts come from summarizeShifts in the parent, so this panel re-fetches
// nothing. The two cards use roomier vertical spacing so together they fill the
// column height alongside the calendar.
export const ShiftSummaryPanel = ({ summary, periodLabel }) => {
  const history = useHistory();

  const rows = [
    { label: "Total Assigned", value: summary.totalAssigned, icon: EventNoteIcon, color: shiftPalette.green },
    { label: "General Shifts", value: summary.general, icon: SHIFT_CATEGORIES.general.icon, color: SHIFT_CATEGORIES.general.color },
    { label: "Night Shifts", value: summary.night, icon: SHIFT_CATEGORIES.night.icon, color: SHIFT_CATEGORIES.night.color },
    { label: "Week Offs", value: summary.weeklyOff, icon: SHIFT_CATEGORIES.weeklyOff.icon, color: SHIFT_CATEGORIES.weeklyOff.color },
    { label: "Holidays", value: summary.holiday, icon: SHIFT_CATEGORIES.holiday.icon, color: SHIFT_CATEGORIES.holiday.color },
  ];

  // Quick Links route directly to the related My Requests screen.
  const quickLinks = [
    {
      label: "Schedule Change",
      icon: SwapHorizIcon,
      to: "/admin/myzone/apply?view=shift-correction",
    },
  ];

  return (
    <Stack spacing={2.5}>
      {/* Shift Summary */}
      <Box sx={{ ...shiftCardSx, p: 3 }}>
        <Typography fontWeight={designWeights.heading} sx={{ color: shiftPalette.text, mb: 2 }}>
          Shift Summary{" "}
          {periodLabel && (
            <Typography component="span" variant="caption" sx={{ color: shiftPalette.subText }}>
              ({periodLabel})
            </Typography>
          )}
        </Typography>
        {rows.map((r, idx) => {
          const Icon = r.icon;
          return (
            <Stack
              key={r.label}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                py: 1.65,
                borderBottom: idx !== rows.length - 1 ? `1px solid ${shiftPalette.border}` : "none",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: `${r.color}1A`,
                    color: r.color,
                  }}
                >
                  <Icon sx={{ fontSize: 17 }} />
                </Box>
                <Typography variant="body2" sx={{ color: shiftPalette.subText }}>
                  {r.label}
                </Typography>
              </Stack>
              <Typography fontWeight={designWeights.heading} sx={{ color: shiftPalette.text }}>
                {r.value}
              </Typography>
            </Stack>
          );
        })}
      </Box>

      {/* Quick Links */}
      <Box sx={{ ...shiftCardSx, p: 3 }}>
        <Typography fontWeight={designWeights.heading} sx={{ color: shiftPalette.text, mb: 1.5 }}>
          Quick Links
        </Typography>
        {quickLinks.map((link) => {
          const Icon = link.icon;
          const clickable = Boolean(link.to);
          return (
            <Stack
              key={link.label}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => clickable && history.push(link.to)}
              sx={{
                py: 1.85,
                cursor: clickable ? "pointer" : "default",
                opacity: clickable ? 1 : 0.55,
                "&:hover": clickable ? { color: shiftPalette.blue } : undefined,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Icon sx={{ fontSize: 18, color: shiftPalette.subText }} />
                <Typography variant="body2">{link.label}</Typography>
              </Stack>
              <ChevronRightIcon sx={{ fontSize: 18, color: shiftPalette.subText }} />
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};
